import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';

export default function Pricing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePurchase = async () => {
    if (!user) {
      navigate('/register');
      return;
    }
    try {
      const data = await api.post<{ url: string }>('/checkout');
      window.location.href = data.url;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed');
    }
  };

  const features = ['f1', 'f2', 'f3', 'f4', 'f5'] as const;

  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="font-display text-5xl font-bold mb-4">{t('pricing.title')}</h1>
        <p className="text-muted text-lg">{t('pricing.subtitle')}</p>
      </motion.div>

      <motion.div
        className="max-w-md mx-auto bg-surface border border-border rounded-xl p-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        whileHover={{ borderColor: 'rgba(99, 102, 241, 0.3)' }}
      >
        <div className="text-center mb-8">
          <h2 className="font-display text-2xl font-semibold mb-2">{t('pricing.planName')}</h2>
          <p className="text-muted text-sm">{t('pricing.planDesc')}</p>
        </div>

        <div className="text-center mb-8">
          <span className="font-display text-6xl font-bold">{t('pricing.price')}</span>
          <span className="text-muted ml-2">{t('pricing.currency')}</span>
          <p className="text-xs text-muted mt-2">{t('pricing.oneTime')}</p>
        </div>

        <ul className="space-y-3 mb-8">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-success flex-shrink-0 mt-0.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{t(`pricing.features.${f}`)}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={handlePurchase}
          className="w-full py-3 rounded bg-primary hover:bg-primary-hover text-white font-medium transition-colors"
        >
          {user ? t('pricing.purchase') : t('pricing.createAccount')}
        </button>

        <p className="text-center text-xs text-muted mt-4">
          {t('pricing.termsNotice')}{' '}
          <a href="/tos" className="underline hover:text-foreground">{t('pricing.terms')}</a>
          {'. '}
          {t('pricing.noRefunds')}
        </p>
      </motion.div>
    </div>
  );
}
